/* Shared layout for legal pages (Terms / Privacy / etc.).

Renders a clean centred document layout with:
  - the existing landing Header
  - a hero band with title + last-updated date
  - a sticky Table of Contents on the left (lg+)
  - the legal content body on the right
  - the existing landing Footer

Pages pass:
  title         — page title (e.g. "Terms of Service")
  lastUpdated   — ISO-ish date string ("30 April 2026")
  intro         — one or two short paragraphs at the top
  sections      — Array<{ id, heading, body: ReactNode }>
*/
import { useEffect } from "react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";

const C = {
  navy: "#0B1426",
  blue: "#022CED",
  gold: "#F0B83A",
  grey: "#475569",
  ink: "#111827",
  lavender: "#EFEDFB",
  lavender2: "#E2DEFA",
  cream: "#F8F7FB",
};

export default function LegalLayout({ title, lastUpdated, intro, sections, testid }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen" data-testid={testid} style={{ background: "#fff" }}>
      <Header />
      <section className="relative overflow-hidden"
        style={{ background: `linear-gradient(180deg, #fff 0%, ${C.cream} 100%)` }}>
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(2,44,237,0.10) 0%, transparent 60%)" }} />
        <div className="cm-container relative pt-24 pb-20">
          <div className="text-[12px] font-bold uppercase tracking-[0.18em]"
            style={{ color: C.blue }}>Legal</div>
          <h1 className="mt-4 max-w-[920px]"
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 800,
              fontSize: "clamp(36px, 5vw, 72px)",
              lineHeight: "1.05",
              letterSpacing: "-0.02em",
              color: C.navy,
            }}>{title}</h1>
          <div className="mt-6 text-[14px]" style={{ color: C.grey }}>
            <span className="font-semibold" style={{ color: C.navy }}>Last updated:</span>{" "}
            {lastUpdated}
          </div>
        </div>
      </section>

      <section className="py-20" style={{ background: "#fff" }}>
        <div className="cm-container grid lg:grid-cols-[260px_1fr] gap-16">
          {/* TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] mb-4"
                style={{ color: C.blue }}>On this page</div>
              <ul className="space-y-2 border-l" style={{ borderColor: C.lavender2 }}>
                {sections.map((s, i) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} data-testid={`legal-toc-${s.id}`}
                      className="block pl-4 py-1.5 text-[13.5px] hover:text-cm-blue transition-colors"
                      style={{ color: C.grey }}>
                      {i + 1}. {s.heading}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Body */}
          <article className="max-w-[820px]">
            {intro && (
              <div className="mb-12 p-6 rounded-[14px] border-l-4"
                style={{ background: C.cream, borderColor: C.gold }}>
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] mb-2"
                  style={{ color: C.gold }}>Plain-English Summary</div>
                <div className="text-[15px] leading-[1.8]" style={{ color: C.ink }}>{intro}</div>
              </div>
            )}
            {sections.map((s, i) => (
              <section key={s.id} id={s.id} className="scroll-mt-24 mb-14"
                data-testid={`legal-section-${s.id}`}>
                <h2 className="text-[24px] lg:text-[28px] font-bold mb-5"
                  style={{ color: C.navy, letterSpacing: "-0.01em" }}>
                  <span style={{ color: C.gold }}>{String(i + 1).padStart(2, "0")}.</span>{" "}
                  {s.heading}
                </h2>
                <div className="legal-prose text-[15px] leading-[1.85]" style={{ color: C.ink }}>
                  {s.body}
                </div>
              </section>
            ))}
            <div className="mt-16 p-6 rounded-[14px] text-[13px] leading-[1.8]"
              style={{ background: C.cream, color: C.grey }}>
              This document is a template prepared by CoinMBX for informational purposes and
              does not constitute legal advice. Please consult qualified counsel before treating
              it as binding.
            </div>
          </article>
        </div>
      </section>

      <Footer />
      {/* Light prose overrides */}
      <style>{`
        .legal-prose p { margin-bottom: 1.1em; }
        .legal-prose ul { list-style: disc; padding-left: 1.4em; margin-bottom: 1.1em; }
        .legal-prose li { margin-bottom: 0.4em; }
        .legal-prose strong { color: ${C.navy}; }
      `}</style>
    </div>
  );
}
